/**
 * GarmentCard - Display scanned garment info
 *
 * Shows order context, what the garment should look like, and redo status.
 * Uses organization context for multi-tenant Shopify domain.
 *
 * Layout: Order header → preview tiles + product name + SKU + size/color → Progress
 *
 * THE PREVIEW. The catcher is holding a garment that has just come off the
 * press and is answering one question: is this the right one? So the card
 * shows the design rendered ON the shirt, one tile per side that has an image
 * on file, rather than the blank-garment thumbnail it used to show. Every rule
 * about which image may stand under which label lives in the shared primitive
 * (`@/components/production/ProductionPreviewTile`) — the source chain, the
 * tier-aware captions, the skeleton, the fallback walk and the fixed
 * footprint. Catching, Print, Pressing, Packing and Film Print all render the
 * same tiles, so "never claim a side you cannot prove" is enforced once.
 *
 * What was here before: a hand-rolled preview state that fell through to
 * `imageUrl`, the blank-garment/catalogue photo, with nothing telling the
 * operator they were looking at a stand-in. That is the bug this replaces.
 */
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, RefreshCw, CheckCircle, Plus, ArrowLeftRight } from 'lucide-react';
import {
  ProductionPreviewStrip,
  buildFinishedGarmentPreviewEntries,
  type ProductionPreviewKind,
} from '@/components/production/ProductionPreviewTile';
import { useOrganization } from '@/contexts/OrganizationContext';
import { cleanGarmentDisplayName } from '@/lib/garment-display-name';
import type { GarmentLookupResult } from '../hooks/useGarmentLookup';
import { cn } from '@/lib/utils';
import { formatOrderNumber } from '@threadheads/shared';
import { PRINT_TECHNOLOGY_SWITCH_REASON } from '@/features/production-tracker/lib/print-technology-switch';
import { CLIENT_QC_REDO_REASON } from '@/features/production-tracker/lib/client-qc-redo';

interface RedoReasonOption {
  value: string;
  label: string;
  description: string;
}

interface GarmentCardProps {
  garment: GarmentLookupResult['garmentUnit'];
  order: GarmentLookupResult['order'];
  alreadyCaught?: boolean;
  redoReasons?: RedoReasonOption[];
  /**
   * Opens the page's lightbox at this tile's image. `sourceIndex` is the
   * position in the entry's chain that ACTUALLY rendered, so the full view
   * opens on the image the catcher is pointing at rather than on a first
   * choice this tile has already watched fail.
   */
  onOpenPreview?: (kind: ProductionPreviewKind, sourceIndex: number) => void;
}

export function GarmentCard({ garment, order, alreadyCaught, redoReasons, onOpenPreview }: GarmentCardProps) {
  const { t } = useTranslation('production');
  const { organization } = useOrganization();

  // Extract store name from shopify_domain (e.g., "threadheads.myshopify.com" -> "threadheads")
  const shopifyStoreName = organization?.shopify_domain?.replace('.myshopify.com', '') ?? null;

  const displayName = cleanGarmentDisplayName(garment.productName, garment.color, garment.size);

  // The garment-preview policy, shared with Print, Pressing and Packing: a
  // standard gets a tile per side that has a mockup on file, a custom gets its
  // one combined customer preview. Pure, so CatcherPage can build its lightbox
  // from the very same call.
  const previewEntries = buildFinishedGarmentPreviewEntries(garment);

  const identity = (
    <div className="min-w-0 space-y-1 lg:w-72 lg:shrink-0">
      <p className="text-lg font-semibold leading-tight">{displayName}</p>
      <p className="font-mono text-sm text-muted-foreground break-all">{garment.sku}</p>
      <div className="flex items-center gap-3 pt-1">
        {garment.size && (
          <Badge variant="secondary" className="text-2xl font-bold px-4 py-2">
            {garment.size}
          </Badge>
        )}
        {garment.color && (
          <span className="text-base font-medium">{garment.color}</span>
        )}
      </div>
    </div>
  );

  return (
    <Card
      className={cn(
        'transition-all',
        garment.isRedo && 'border-l-4 border-l-amber-500'
      )}
    >
      <CardContent className="pt-6">
        {/* Already caught detail banner */}
        {alreadyCaught && (
          <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-700 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">{t('garmentCard.caught')}</span>
              </div>
              {garment.caughtAt && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400">
                  {formatTimeAgo(garment.caughtAt, t)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="secondary" className="text-xs">
                {garment.status === 'IN_BIN' ? t('garmentCard.inBin') : garment.status === 'CAUGHT' ? t('garmentCard.shelf') : garment.status}
              </Badge>
              {order.binLabel && (
                <Badge variant="outline" className="text-xs">
                  {order.binLabel}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Order header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">{formatOrderNumber(order.orderName)}</span>
              {shopifyStoreName && (
                <a
                  href={`https://admin.shopify.com/store/${shopifyStoreName}/orders?query=${order.orderName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={t('garmentCard.viewInShopify', { orderName: order.orderName })}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
            {order.customerName && (
              <p className="text-sm text-muted-foreground">{order.customerName}</p>
            )}
            {/* BLU-256: Batch number and order date metadata */}
            {(order.batchNumber != null || order.orderDate) && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {order.batchNumber != null && t('garmentCard.batchNumber', { number: order.batchNumber })}
                {order.batchNumber != null && order.orderDate && ' \u00B7 '}
                {order.orderDate && t('orderedDate', { date: formatShortDate(order.orderDate) })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {garment.isRedo && (
              garment.redoReason === 'order_change_backfill' ? (
                <Badge variant="outline" className="flex items-center gap-1 border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-300">
                  <Plus className="h-3 w-3" />
                  {t('orderChange.badge')}
                </Badge>
              ) : garment.redoReason === PRINT_TECHNOLOGY_SWITCH_REASON ? (
                // BLU-1412: switched before it was printed, not a defect. The
                // scan payload carries no print technology, so the label names
                // the switch rather than its direction.
                <Badge variant="outline" className="flex items-center gap-1 border-blue-400 text-blue-700 dark:border-blue-600 dark:text-blue-300">
                  <ArrowLeftRight className="h-3 w-3" />
                  {t('printTechSwitch.badgeGeneric')}
                </Badge>
              ) : (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <RefreshCw className="h-3 w-3" />
                  REDO
                </Badge>
              )
            )}
            <Badge variant="outline">
              {t('itemOf', { index: garment.itemIndex, total: garment.totalInOrder })}
            </Badge>
          </div>
        </div>

        {/* Product details: what it should look like, beside who it is.
            ONE layout for customs and standards — the strip decides how many
            tiles a garment earns, fills this column with them, and takes its
            height from that width rather than from the image, so an image that
            arrives late or falls back cannot move the identity zone or the
            accept controls under the operator's hand mid-scan.

            The PICTURE is the evidence and the text is the label on it, so on
            a wide card the preview column takes the room and the identity zone
            is pinned, exactly as on the print station's card. A fixed-width
            strip beside a flex-1 text column gave the thing being verified a
            quarter of the width and its caption three quarters. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="min-w-0 lg:flex-1">
            {/* No shape prop, and no sizing classes here: on a fill variant
                the shape is the tile COUNT, which the strip knows. A custom's
                one combined preview and a one-sided standard's single tile
                come out identical, both filling this column. */}
            <ProductionPreviewStrip
              entries={previewEntries}
              subject={displayName}
              variant="comparison"
              onOpen={onOpenPreview}
            />
          </div>
          {identity}
        </div>

        {/* Redo reason if applicable */}
        {garment.isRedo && garment.redoReason && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {t('garmentCard.redoReason')}
            </p>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mt-1">
              {garment.redoReason === 'order_change_backfill'
                ? t('catcher.orderChangeReason')
                : garment.redoReason === PRINT_TECHNOLOGY_SWITCH_REASON
                  ? t('printTechSwitch.reasonGeneric')
                  : garment.redoReason === CLIENT_QC_REDO_REASON
                    ? t('clientQcRedo.reason')
                    : formatRedoReason(garment.redoReason, redoReasons)}
            </p>
          </div>
        )}

        {/* Order progress */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('garmentCard.orderProgress')}</span>
            <span className="font-medium">
              {t('garmentCard.caughtProgress', { caught: order.itemsCaught, total: order.totalPodItems + order.totalPapItems })}
            </span>
          </div>
          <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{
                width: `${Math.min(100, (order.itemsCaught / (order.totalPodItems + order.totalPapItems)) * 100)}%`,
              }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatShortDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
}

function formatTimeAgo(isoString: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return t('common:time.justNow');
  if (minutes < 60) return t('common:time.minutesAgo_other', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('common:time.hoursAgo_other', { count: hours });
  const days = Math.floor(hours / 24);
  return t('common:time.daysAgo_other', { count: days });
}

function formatRedoReason(reason: string, redoReasons?: RedoReasonOption[]): string {
  // Look up from org-configured reasons first
  if (redoReasons) {
    const match = redoReasons.find(r => r.value === reason);
    if (match) return match.label;
  }
  // Fall back: replace underscores with spaces, title case
  return reason
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
